import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  fetchTodos,
  deleteTodo,
  updateTodo,
  reorderTodo,
  persistReorder,
  selectTodosLoading,
  selectTodosInitialFetchDone,
  selectTodosByStatus,
  selectTodosFilters,
  setFilters,
} from 'store/slices/todosSlice';
import { TODO_PRIORITY_OPTIONS } from 'constants/todos';
import Page from 'components/layout/Page/Page';
import { PrimaryButton } from 'components/buttons';
import Modal from 'components/layout/modals/Modal/Modal';
import DeleteModal from 'components/layout/modals/DeleteModal/DeleteModal';
import { FilterBar, useFilterBar } from 'components/filter';
import TodoTabs from './components/TodoTabs/TodoTabs';
import TodoKanbanBoard from './components/TodoKanbanBoard/TodoKanbanBoard';
import AttachTodoToReminder from './components/AttachTodoToReminder/AttachTodoToReminder';

const TODO_PRIORITY_FILTER_OPTIONS = [
  { value: 'ALL', label: 'All' },
  ...TODO_PRIORITY_OPTIONS,
];

const TODO_LIST_FILTERS = [
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Search by todo title',
    tier: 'primary',
  },
  {
    key: 'priority',
    type: 'select',
    label: 'Priority',
    options: TODO_PRIORITY_FILTER_OPTIONS,
    tier: 'primary',
  },
];

const TODO_LIST_FILTER_CONFIG = {
  route: '/todos',
  filters: {
    priority: {
      type: 'select',
      urlKey: 'priority',
      default: 'ALL',
      options: TODO_PRIORITY_FILTER_OPTIONS,
    },
    search: {
      type: 'search',
      urlKey: 'search',
      default: '',
    },
  },
  fetchThunk: fetchTodos,
  setFilters,
  selectFilters: selectTodosFilters,
  selectLoading: selectTodosLoading,
  selectInitialFetchDone: selectTodosInitialFetchDone,
};

function Todos() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const todosByStatus = useSelector(selectTodosByStatus);
  const loading = useSelector(selectTodosLoading);
  const {
    filterValues,
    isInitialLoading,
    hasActiveFilters,
    applyFilters,
    resetFilters,
  } = useFilterBar(TODO_LIST_FILTER_CONFIG);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [todoToDelete, setTodoToDelete] = useState(null);
  const [attachReminderModalOpen, setAttachReminderModalOpen] = useState(false);
  const [todoToAttach, setTodoToAttach] = useState(null);

  const handleDeleteClick = (todo) => {
    setTodoToDelete(todo);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (todoToDelete) {
      try {
        await dispatch(deleteTodo(todoToDelete._id)).unwrap();
        setDeleteModalOpen(false);
        setTodoToDelete(null);
      } catch (error) {
        console.error('Failed to delete todo:', error);
      }
    }
  };

  const handleAction = (action, item) => {
    const id = item._id;
    switch (action) {
      case 'edit':
        navigate(`edit?id=${id}`);
        break;
      case 'delete':
        handleDeleteClick(item);
        break;
      case 'attach-to-reminder':
        setTodoToAttach(item);
        setAttachReminderModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleStatusChange = (todoId, newStatus, beforeId = null) => {
    dispatch(updateTodo({ id: todoId, data: { status: newStatus } }));
    dispatch(reorderTodo({ id: todoId, beforeId }));
    dispatch(persistReorder({ todoId, beforeId }));
  };

  const handleReorder = (todoId, beforeId) => {
    dispatch(reorderTodo({ id: todoId, beforeId }));
    dispatch(persistReorder({ todoId, beforeId }));
  };

  return (
    <Page title="Todos" headerActions={<TodoTabs />}>
      <div className="px-4 py-4 sm:px-6">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex-1">
            <FilterBar
              filters={TODO_LIST_FILTERS}
              values={filterValues}
              onChange={applyFilters}
              onReset={resetFilters}
              hasActiveFilters={hasActiveFilters}
              loading={isInitialLoading}
            />
          </div>
          <PrimaryButton
            icon={PlusCircleIcon}
            loading={isInitialLoading}
            onClick={() => navigate('add')}
          >
            Create todo
          </PrimaryButton>
        </div>

        <TodoKanbanBoard
          todosByStatus={todosByStatus}
          loading={isInitialLoading}
          onStatusChange={handleStatusChange}
          onReorder={handleReorder}
          onAction={handleAction}
          onMove={handleStatusChange}
        />
      </div>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Todo"
        message={`Are you sure you want to delete todo "${todoToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        loading={loading}
      />

      <Modal
        isOpen={attachReminderModalOpen}
        onClose={() => setAttachReminderModalOpen(false)}
        className="sm:max-w-xl"
      >
        <div className="relative flex h-auto w-full flex-col bg-gray-800 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
          <button
            type="button"
            onClick={() => setAttachReminderModalOpen(false)}
            className="absolute right-4 top-4 rounded-md text-gray-400 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <span className="sr-only">Close</span>
            <XMarkIcon className="h-6 w-6 cursor-pointer" aria-hidden="true" />
          </button>
          <h3 className="mb-4 text-base font-semibold leading-6 text-white">Attach to Reminder</h3>
          <AttachTodoToReminder
            key={todoToAttach?._id}
            todoId={todoToAttach?._id}
            onSuccess={() => setAttachReminderModalOpen(false)}
            onCancel={() => setAttachReminderModalOpen(false)}
            showTodoInfo={true}
            className="flex-1"
          />
        </div>
      </Modal>
    </Page>
  );
}

export default Todos;
